import { ArrowRightIcon } from '@heroicons/react/24/outline'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import type { IUser } from '../../../../Model/index.js'
import { LicenseTier, userLicense } from '../../../../Model/index.js'
import { Link } from 'react-router-dom'
import { LicenseBadge } from '../../../Common/Tier2Badge/Tier2Badge.tsx'
import { OrganizationLogo } from '../../../Component/Organization/OrganizationLogo.tsx'
import { DASHBOARD_WIDGET_CLASS } from './dashboardUtils.ts'
import { organizationSlice } from '../../../../Flux/Slice/Organization/OrganizationSlice.js'
import { useGetOrganizationByIdSelector } from '../../../../Flux/Selector/Organization/GetOrganizations/GetOrganizationsSelector.js'

export const OrganizationOverviewWidget = ({ user }: { user: IUser }) => {
  const dispatch = useDispatch()
  /**
   * `user.org` is assembled claim-by-claim from the Keycloak token in `mapTokenInfoToUser`, and there
   * is no `logo` or `description` claim to assemble them from — so both are always undefined on it and
   * the widget has to fetch the organisation to show either. Rendering falls back to `user.org` until
   * that resolves, which is what the initials placeholder is for.
   */
  const fetchedOrg = useGetOrganizationByIdSelector(user.org.id)
  const org = fetchedOrg ?? user.org

  useEffect(() => {
    if (!fetchedOrg) {
      dispatch(organizationSlice.actions.getOrganizationRequest({ organizationId: user.org.id }))
    }
  }, [dispatch, fetchedOrg, user.org.id])

  const orgLink = `/organization/${user.org.id}`
  const license = userLicense(user)
  const tier = license?.tier ?? LicenseTier.BASE

  return (
    <section className={`${DASHBOARD_WIDGET_CLASS} xl:col-span-5`}>
      <div className="flex h-full flex-col gap-5 sm:flex-row sm:items-center">
        <Link
          to={orgLink}
          className="flex w-full max-w-[12rem] shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-4"
        >
          <OrganizationLogo organization={org} />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <LicenseBadge key={`${license?.tier}`} licenseTier={tier} orientation="relative" />
          </div>
          <p className="text-xl font-semibold text-slate-900">{org.name}</p>
          {org.description ? <p className="mt-2 max-h-16 overflow-hidden text-sm text-slate-600">{org.description}</p> : null}
          <div className="mt-4">
            <Link to={orgLink} className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900">
              Open organization
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}
